import { Avatar, DownloadFile, EmptyState, NoDataIcon } from '@lmc/ui-kit'
import { Heading, Text } from '@lmc/ui-kit/src/general'
import { Drawer, Pagination } from 'antd'
import { showToast } from 'common/utils'
import { Spinner } from 'components/generic'
import type { Dayjs } from 'dayjs'
import useMixpanelEvent, { MixpanelEvents } from 'hooks/useMixpanelEvent'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useChargeReportDrawer } from '../hooks/useChargeReport'
import { PayReportCsvButton } from '../PayReport/PayReportBreakdownFooter'
import type { ChargeReportRow } from '../types/chargeReport'
import { ChargeReportBreakdownTable } from './ChargeReportBreakdownTable'
import { downloadChargeReportCsv } from './downloadChargeReportCsv'

type ChargeReportBreakdownDrawerProps = {
  open: boolean
  onClose: () => void
  dateRange: [Dayjs, Dayjs]
  row: ChargeReportRow | null
}

export const ChargeReportBreakdownDrawer = ({
  open,
  onClose,
  dateRange,
  row
}: ChargeReportBreakdownDrawerProps) => {
  const { t } = useTranslation()
  const sendEvent = useMixpanelEvent()
  const [drawerPage, setDrawerPage] = useState(1)

  const drawerParams = row
    ? {
        rowId: row.id,
        startTime: dateRange[0].startOf('day').toISOString(),
        endTime: dateRange[1].endOf('day').toISOString(),
        page: drawerPage,
        limit: 10
      }
    : null
  const { data, isFetching } = useChargeReportDrawer(
    drawerParams,
    open && Boolean(row)
  )

  const summary = data?.summary
  const rows = data?.rows ?? []
  const pagination = data?.pagination

  const rangeLabel = `${dateRange[0].format('DD-MM-YYYY')} - ${dateRange[1].format('DD-MM-YYYY')}`

  const handleDownloadCsv = async () => {
    if (!row) {
      return
    }
    try {
      await downloadChargeReportCsv({
        startTime: dateRange[0].startOf('day').toISOString(),
        endTime: dateRange[1].endOf('day').toISOString(),
        reportType: 'breakdown',
        serviceUserIds: [row.serviceUser.id],
        ...(row.fundingType ? { fundingTypeIds: [row.fundingType.id] } : {}),
        fileName: 'charge-report-breakdown.csv'
      })
      sendEvent(MixpanelEvents.CHARGE_REPORT_BREAKDOWN_CSV_DOWNLOADED, {
        numberOfDays: dateRange[1].diff(dateRange[0], 'day') + 1,
        serviceUserId: row.serviceUser.id,
        funderName: row.fundingType?.name ?? null
      })
    } catch (error) {
      console.error('Failed to download CSV:', error)
      showToast({ title: t('SOMETHING_WENT_WRONG'), type: 'error' })
    }
  }

  const eventBreakdownText = summary?.eventTypeBreakdown
    .map((e) =>
      t('CHARGE_REPORT_DRAWER_EVENT_BREAKDOWN', {
        count: e.count,
        name: e.name
      })
    )
    .join(', ')

  return (
    <StyledDrawer
      data-testid="charge-report-su-drawer"
      title={
        row ? (
          <HeaderBlock>
            <RangeMuted level={2}>{rangeLabel}</RangeMuted>
            <ProfileRow>
              <Avatar url={row.serviceUser.avatarUrl ?? undefined} size={32} />
              <Heading level={4}>
                {row.serviceUser.name}
                {row.fundingType ? ` · ${row.fundingType.name}` : ''}
              </Heading>
            </ProfileRow>
          </HeaderBlock>
        ) : null
      }
      placement="right"
      width={910}
      onClose={onClose}
      open={open && Boolean(row)}
      destroyOnClose
      footer={
        row ? (
          <FooterBar>
            <FooterSummary>
              <Text weight="bold">
                {t('CHARGE_REPORT_DRAWER_FOOTER_TOTAL', {
                  count: summary?.totalVisits ?? 0
                })}
              </Text>
              {eventBreakdownText ? (
                <MutedInline> ({eventBreakdownText})</MutedInline>
              ) : null}
            </FooterSummary>
            <PayReportCsvButton
              data-testid="charge-report-drawer-download-csv-btn"
              icon={<DownloadFile />}
              onClick={handleDownloadCsv}
            >
              {t('PAY_REPORT_DOWNLOAD_CSV')}
            </PayReportCsvButton>
          </FooterBar>
        ) : null
      }
    >
      {row ? (
        <DrawerInner>
          <SummaryBar>
            <SummaryItem>
              <SummaryLabel level={2}>
                {t('CHARGE_REPORT_DRAWER_STATS_TOTAL_CHARGE')}
              </SummaryLabel>
              <SummaryValue>
                {(summary?.totalReconciledCharge ?? 0).toFixed(2)}{' '}
                {t('RECONCILIATION_GBP_SUFFIX')}
              </SummaryValue>
            </SummaryItem>
            <SummaryItem>
              <SummaryLabel level={2}>
                {t('CHARGE_REPORT_DRAWER_STATS_TOTAL_HOURS')}
              </SummaryLabel>
              <SummaryValue>
                {summary?.totalReconciledHours ?? 0}{' '}
                {t('CHARGE_REPORT_DRAWER_HOURS_UNIT', {
                  count: summary?.totalReconciledHours ?? 0
                })}
              </SummaryValue>
            </SummaryItem>
          </SummaryBar>

          <TableWrap>
            {rows.length ? (
              <ChargeReportBreakdownTable rows={rows} loading={isFetching} />
            ) : isFetching ? (
              <EmptyStateWrap>
                <Spinner />
              </EmptyStateWrap>
            ) : (
              <EmptyStateWrap>
                <EmptyState
                  icon={<NoDataIcon />}
                  title={t('NO_DATA_TO_SHOW')}
                />
              </EmptyStateWrap>
            )}
          </TableWrap>

          {pagination && pagination.totalItems > pagination.itemsPerPage ? (
            <PaginationWrap>
              <Pagination
                current={pagination.currentPage}
                total={pagination.totalItems}
                pageSize={pagination.itemsPerPage}
                onChange={(p) => setDrawerPage(p)}
                showSizeChanger={false}
              />
            </PaginationWrap>
          ) : null}
        </DrawerInner>
      ) : null}
    </StyledDrawer>
  )
}

const StyledDrawer = styled(Drawer)`
  .ant-drawer-header {
    padding: 20px 24px;
    border-bottom: 1px solid ${({ theme }) => theme.color.neutral.color8};
    box-shadow: none;
  }

  .ant-drawer-header-title {
    flex-direction: row-reverse;
    align-items: flex-start;
  }

  .ant-drawer-close {
    margin: 0;
    padding: 4px;
  }

  .ant-drawer-footer {
    padding: 24px;
    box-shadow: 0px -2px 4px 0px rgba(0, 0, 0, 0.1);
  }
`

const DrawerInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const HeaderBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-right: 8px;
`

const RangeMuted = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color5};
`

const ProfileRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const SummaryBar = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px 24px;
  padding: 20px 24px;
  border-radius: 16px;
  background: ${({ theme }) => theme.color.neutral.color10};
`

const SummaryItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`

const SummaryLabel = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color5};
`

const SummaryValue = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
  font-weight: 600;
`

const TableWrap = styled.div`
  width: 100%;
  min-width: 0;
  border-radius: 20px;
  overflow: hidden;
`

const EmptyStateWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 320px;
  width: 100%;
  border-radius: 20px;
  background: ${({ theme }) => theme.color.neutral.color10};
`

const PaginationWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 8px 0 0;
`

const FooterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`

const FooterSummary = styled.div`
  flex: 1;
  min-width: 0;
  color: ${({ theme }) => theme.color.neutral.color3};
`

const MutedInline = styled.span`
  color: ${({ theme }) => theme.color.neutral.color5};
`
